/**
 * NotFoundPage — real 404 route for unmatched URLs.
 *
 * Phase L.2 P1 batch fix for audit finding NF-1:
 *   Previously `App.js` catch-all was `<Navigate to="/" replace />`
 *   — every bad URL silently redirected home, erasing the URL in
 *   history and giving users no feedback that the link was broken.
 *   This page renders a real 404 experience instead.
 *
 * Semantics:
 *   - Wrapped in `PublicPageShell` (which provides the `<main>`
 *     landmark) so screen-reader users get a proper landmark hop.
 *   - `<h1>` = "Page not found" — the WCAG-aligned unambiguous
 *     phrase (SC 2.4.6). We keep it dry rather than brand-flavored
 *     so a screen reader immediately understands what happened.
 *   - Body copy explains the URL is invalid + offers three
 *     helpful CTAs (home / contact / map).
 *   - Fires a `not_found_viewed` funnel event via the existing
 *     `trackRecruitEvent` helper on mount so analytics can catch
 *     broken external links pointing at us. No new tracking helper
 *     is introduced.
 *
 * Does NOT redirect. Rendering the page IS the 404 experience;
 * the HTTP status is a router-level concern that this SPA doesn't
 * currently expose (the whole app ships as `index.html`).
 */
import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Home, MessageCircle, MapPin } from "lucide-react";
import PublicPageShell from "@/components/PublicPageShell";
import { trackRecruitEvent } from "@/lib/recruitTracking";

export default function NotFoundPage() {
  const location = useLocation();
  useEffect(() => {
    // Cap the reported path so we never blow past the recruit-event
    // string cap. `pathname` alone is enough for admin triage.
    const path = (location?.pathname || "").slice(0, 128);
    trackRecruitEvent("not_found_viewed", { source: path });
  }, [location?.pathname]);

  return (
    <PublicPageShell mainClass="flex items-center justify-center px-6 py-16 md:py-24">
      <section
        className="pp-wrap max-w-2xl mx-auto text-center"
        data-testid="not-found-page"
      >
        <span className="pp-eyebrow" data-testid="not-found-eyebrow">
          <span className="dot" />
          404
        </span>
        <h1
          className="pp-h1 mt-6"
          data-testid="not-found-heading"
        >
          Page not found
        </h1>
        <p
          className="pp-sub mt-5 mx-auto max-w-xl"
          data-testid="not-found-body"
        >
          The link you followed doesn&apos;t match anything on PooPluckers.
          It might be mistyped, or the page may have moved. Here are a few
          places that could help.
        </p>
        <div
          className="mt-10 flex flex-wrap items-center justify-center gap-3"
          data-testid="not-found-cta-row"
        >
          <Link
            to="/"
            className="pp-btn pp-btn-primary"
            data-testid="not-found-home-link"
          >
            <Home className="h-4 w-4" aria-hidden="true" />
            Go home
            <ArrowRight className="arrow h-4 w-4" aria-hidden="true" />
          </Link>
          <Link
            to="/contact"
            className="pp-btn pp-btn-ghost"
            data-testid="not-found-contact-link"
          >
            <MessageCircle className="h-4 w-4" aria-hidden="true" />
            Contact us
          </Link>
          <Link
            to="/map"
            className="pp-btn pp-btn-ghost"
            data-testid="not-found-map-link"
          >
            <MapPin className="h-4 w-4" aria-hidden="true" />
            Map coverage
          </Link>
        </div>
      </section>
    </PublicPageShell>
  );
}
